/**
 * Yard clutter for the unreachable back gardens (lifted from lantern-water's
 * `backdrop-props.js`): washing lines, woodpiles, kitchen plots, hedges, rubble
 * walls, barrels, crates, carts, a well, wall stairs, gates, yard trees,
 * bushes, sheds and alley shade. Everything is a handful of boxes and quads:
 * atlas surfaces go into the builder's `AtlasKit`, timber and stone reuse the
 * town's materials, so no draw is added. Random draws are the builder's.
 */
import * as THREE from 'three';
import { CELLS as C, type Sub, type V3 } from './backdrop-art.ts';
import type { BackdropBuilder } from './backdrop-kit.ts';
import type { Ctx } from './kit.ts';

/** Sample only the middle of a cell: mip levels bleed neighbouring cells in. */
const INNER: Sub = [0.15, 0.15, 0.85, 0.85];

export type BackdropProps = ReturnType<typeof createBackdropProps>;

export const createBackdropProps = (ctx: Ctx, b: BackdropBuilder) => {
  const { m, box, cylinder, line, mesh, leafMats, plane } = ctx;
  const { kit, random } = b;
  const leaf0 = leafMats[0];
  const leaf1 = leafMats[1];

  /** A washing line between two posts, with cloths sampled from the laundry cell. */
  const laundry = (p: THREE.Object3D, ax: number, az: number, bx: number, bz: number, y: number, count = 4, posts = true): void => {
    const h = 1.55;
    if (posts) {
      for (const [x, z] of [
        [ax, az],
        [bx, bz],
      ] as const)
        box(p, x, y + h / 2, z, 0.09, h, 0.09, m.darkWood);
    }
    line(p, [ax, y + h - 0.08, az], [bx, y + h - 0.08, bz], 0.022, m.iron);
    const len = Math.hypot(bx - ax, bz - az);
    const dx = (bx - ax) / len;
    const dz = (bz - az) / len;
    const nx = -dz;
    const nz = dx;
    const flip = nz + nx * 0.5 < 0 ? -1 : 1;
    for (let i = 0; i < count; i += 1) {
      const t = (i + 0.5) / count + (random() - 0.5) * 0.05;
      const w = Math.min(0.62, (len / count) * 0.8) * (0.8 + random() * 0.25);
      const hh = 0.5 + random() * 0.35;
      const cx = ax + (bx - ax) * t;
      const cz = az + (bz - az) * t;
      const top = y + h - 0.1 - Math.sin(t * Math.PI) * 0.08;
      const k = Math.floor(random() * 4);
      const hx = (dx * w) / 2;
      const hz = (dz * w) / 2;
      const sway = (random() - 0.5) * 0.08;
      const a: V3 = [cx - hx * flip + nx * sway, top - hh, cz - hz * flip + nz * sway];
      const bb: V3 = [cx + hx * flip + nx * sway, top - hh, cz + hz * flip + nz * sway];
      const c: V3 = [cx + hx * flip, top, cz + hz * flip];
      const d: V3 = [cx - hx * flip, top, cz - hz * flip];
      const sub: Sub = [k * 0.25, 0, k * 0.25 + 0.24, hh / 0.85];
      kit.quad(a, bb, c, d, C.laundry, [1, 1, 1], sub, [0.86, 1]);
      kit.quad(bb, a, d, c, C.laundry, [0.8, 0.8, 0.82], sub, [0.8, 0.9]);
    }
  };

  /** Stacked firewood under a small lean-to. */
  const woodpile = (p: THREE.Object3D, x: number, y: number, z: number, w = 1.6, face: 'south' | 'east' = 'south'): void => {
    const h = 0.95;
    const d = 0.6;
    if (face === 'south') kit.box(x, y + h / 2, z, w, h, d, { top: C.bark, front: C.logs, east: C.logs, west: C.logs, back: null });
    else kit.box(x, y + h / 2, z, d, h, w, { top: C.bark, front: C.logs, east: C.logs, west: null, back: null });
    const g = new THREE.Group();
    g.position.set(x, y + h + 0.22, z);
    if (face !== 'south') g.rotation.y = -Math.PI / 2;
    p.add(g);
    const lean = box(g, 0, 0, -0.05, w + 0.3, 0.07, d + 0.45, m.roofDark);
    lean.rotation.x = 0.28;
    for (const sx of [-1, 1]) box(g, (sx * (w + 0.1)) / 2, -0.55, 0.25, 0.08, 1.1, 0.08, m.darkWood);
  };

  /** A planted kitchen garden: soil tiles with rows, framed by boards, leafy crowns and canes. */
  const vegPlot = (p: THREE.Object3D, x: number, y: number, z: number, w: number, d: number, cell: number = C.vegRows): void => {
    const nx = Math.max(1, Math.round(w / 1.25));
    const nz = Math.max(1, Math.round(d / 1.6));
    for (let i = 0; i < nx; i += 1) {
      for (let j = 0; j < nz; j += 1) {
        const x0 = x - w / 2 + (i * w) / nx;
        const x1 = x0 + w / nx;
        const z1 = z + d / 2 - (j * d) / nz;
        const z0 = z1 - d / nz;
        kit.quad([x0, y + 0.06, z1], [x1, y + 0.06, z1], [x1, y + 0.06, z0], [x0, y + 0.06, z0], (i + j) % 3 === 2 && cell === C.vegRows ? C.cabbage : cell);
      }
    }
    for (const s of [-1, 1]) {
      box(p, x, y + 0.06, z + (s * d) / 2, w + 0.1, 0.12, 0.08, m.wood);
      box(p, x + (s * w) / 2, y + 0.06, z, 0.08, 0.12, d, m.wood);
    }
    const rows = Math.max(2, Math.round(d / 0.8));
    for (let j = 0; j < rows; j += 1) {
      for (let i = 0; i < Math.max(1, Math.round(w / 1.1)); i += 1) {
        if (random() < 0.35) continue;
        const cx = x - w / 2 + ((i + 0.5) * w) / Math.max(1, Math.round(w / 1.1));
        const cz = z - d / 2 + ((j + 0.5) * d) / rows;
        const leaf = mesh(p, plane, leaf0, cx, y + 0.24, cz, 0.7, 0.4, 1);
        leaf.rotation.set(-0.35, (random() - 0.5) * 0.5, 0);
      }
    }
    if (random() < 0.7) {
      for (let i = 0; i < 4; i += 1) {
        const xx = x - w * 0.35 + (i * w * 0.7) / 3;
        line(p, [xx - 0.12, y, z - d * 0.3], [xx, y + 1.0, z - d * 0.3], 0.03, m.wood);
        line(p, [xx + 0.12, y, z - d * 0.3], [xx, y + 1.0, z - d * 0.3], 0.03, m.wood);
      }
    }
  };

  /** A clipped hedge: an even body and a crown of offset cushions. */
  const hedge = (p: THREE.Object3D, x: number, y: number, z: number, w: number, d: number, h = 0.8): void => {
    const along = w >= d;
    const len = along ? w : d;
    const n = Math.max(2, Math.round(len / 0.9));
    const faces = { top: C.hedge, front: C.hedge, east: C.hedge, west: C.hedge, back: null };
    const thick = along ? d : w;
    const bh = h * 0.72;
    const seg = len / n;
    const at = (t: number): readonly [number, number] => (along ? [x + t, z] : [x, z + t]);
    for (let i = 0; i < n; i += 1) {
      const [hx, hz] = at((i + 0.5) * seg - len / 2);
      const [bw, bd] = along ? [seg + 0.02, thick] : [thick, seg + 0.02];
      kit.box(hx, y + bh / 2, hz, bw, bh, bd, faces, [0.78, 0.82, 0.7], [0.34, 0.86], INNER);
    }
    for (let i = 0; i <= n; i += 1) {
      const tpos = Math.min(len - 0.35, Math.max(0.35, i * seg + (random() - 0.5) * 0.2)) - len / 2;
      const [hx, hz] = at(tpos);
      const ch = h * (0.2 + random() * 0.14);
      const cl = seg * (0.75 + random() * 0.3);
      const ct = thick * (0.7 + random() * 0.12);
      const [cw, cd] = along ? [cl, ct] : [ct, cl];
      kit.box(hx, y + bh + ch / 2 - 0.03, hz, cw, ch, cd, faces, [0.8, 0.84, 0.72], [0.8, 0.95], INNER);
      if (i % 2 === 0) {
        const leaf = mesh(p, plane, leaf0, hx, y + bh + ch + 0.05, hz + (along ? thick * 0.25 : 0), cl * 1.3, 0.45, 1);
        leaf.rotation.set(-0.3, (random() - 0.5) * 0.4, 0);
      }
    }
  };

  /** A rubble garden wall with a cream coping. */
  const lowWall = (p: THREE.Object3D, ax: number, az: number, bx: number, bz: number, y: number, h = 0.7): void => {
    const len = Math.hypot(bx - ax, bz - az);
    const n = Math.max(1, Math.round(len / 1.25));
    for (let i = 0; i < n; i += 1) {
      const t0 = i / n;
      const t1 = (i + 1) / n;
      const x0 = ax + (bx - ax) * t0;
      const z0 = az + (bz - az) * t0;
      const x1 = ax + (bx - ax) * t1;
      const z1 = az + (bz - az) * t1;
      kit.box((x0 + x1) / 2, y + h / 2, (z0 + z1) / 2, Math.abs(x1 - x0) + 0.3, h, Math.abs(z1 - z0) + 0.3, { top: C.drystone, front: C.drystone, east: C.drystone, west: C.drystone, back: C.drystone }, [1, 1, 1], [0.65, 1]);
    }
    box(p, (ax + bx) / 2, y + h + 0.04, (az + bz) / 2, Math.abs(bx - ax) + 0.38, 0.08, Math.abs(bz - az) + 0.38, m.cream);
  };

  const barrel = (p: THREE.Object3D, x: number, y: number, z: number, s = 0.42): void => {
    cylinder(p, x, y + s * 0.6, z, s * 0.5, s * 1.2, m.wood, 0.92);
    cylinder(p, x, y + s * 0.95, z, s * 0.52, 0.05, m.iron);
    cylinder(p, x, y + s * 0.25, z, s * 0.52, 0.05, m.iron);
  };

  const crate = (p: THREE.Object3D, x: number, y: number, z: number, s = 0.55): void => {
    const c = box(p, x, y + s / 2, z, s, s, s, m.wood);
    c.rotation.y = (random() - 0.5) * 0.5;
  };

  const cart = (p: THREE.Object3D, x: number, y: number, z: number, rot = 0): void => {
    const g = new THREE.Group();
    g.position.set(x, y, z);
    g.rotation.y = rot;
    p.add(g);
    box(g, 0, 0.62, 0, 1.6, 0.1, 0.95, m.wood);
    for (const s of [-1, 1]) box(g, 0, 0.82, s * 0.45, 1.6, 0.34, 0.06, m.wood);
    box(g, 0.78, 0.82, 0, 0.06, 0.34, 0.9, m.wood);
    for (const s of [-1, 1]) {
      const wheel = cylinder(g, 0.1, 0.42, s * 0.55, 0.4, 0.08, m.darkWood);
      wheel.rotation.x = Math.PI / 2;
    }
    line(g, [-0.8, 0.62, 0.3], [-2.0, 0.08, 0.35], 0.06, m.wood);
    line(g, [-0.8, 0.62, -0.3], [-2.0, 0.08, -0.25], 0.06, m.wood);
    // The prototype's straw load sits in the cart group's space; the atlas kit is in world space, so it is placed there.
    const load = new THREE.Vector3(0.1, 0.84, 0).applyAxisAngle(new THREE.Vector3(0, 1, 0), rot);
    const across = Math.abs(Math.sin(rot)) > 0.7;
    kit.box(x + load.x, y + load.y, z + load.z, across ? 0.7 : 1.2, 0.3, across ? 1.2 : 0.7, { top: C.straw, front: null, east: null, west: null, back: null });
  };

  const well = (p: THREE.Object3D, x: number, y: number, z: number): void => {
    cylinder(p, x, y + 0.4, z, 0.62, 0.8, m.stone);
    cylinder(p, x, y + 0.82, z, 0.66, 0.08, m.cream);
    cylinder(p, x, y + 0.83, z, 0.5, 0.04, m.dark);
    for (const s of [-1, 1]) box(p, x + s * 0.58, y + 1.2, z, 0.1, 1.5, 0.1, m.darkWood);
    const g = new THREE.Group();
    g.position.set(x, y + 2.0, z);
    p.add(g);
    b.gableRoof(g, 0, 0, 0, 1.3, 0.9, 0.4, 'x', m.roof, 0.1);
    line(p, [x - 0.58, y + 1.55, z], [x + 0.58, y + 1.55, z], 0.07, m.wood);
    box(p, x, y + 1.2, z, 0.02, 0.7, 0.02, m.iron);
    box(p, x, y + 0.95, z, 0.22, 0.22, 0.22, m.wood);
  };

  /** A stone stair climbing along a wall to a door that is never reachable; returns its top. */
  const wallStair = (p: THREE.Object3D, x: number, y: number, z: number, steps: number, dir = 1, axis: 'x' | 'z' = 'x', tread = 0.42, rise = 0.24, width = 0.9): number => {
    for (let i = 0; i < steps; i += 1) {
      const top = y + (i + 1) * rise;
      const along = (i + 0.5) * tread * dir;
      if (axis === 'x') box(p, x + along, (y + top) / 2, z, tread + 0.01, top - y, width, m.stone);
      else box(p, x, (y + top) / 2, z + along, width, top - y, tread + 0.01, m.stone);
    }
    const end = steps * tread * dir;
    const top = y + steps * rise;
    if (axis === 'x') {
      box(p, x + end + dir * 0.45, top - 0.1, z, 0.9, 0.2, width, m.stone);
      line(p, [x, y + 0.9, z + width / 2], [x + end, top + 0.9, z + width / 2], 0.04, m.iron);
    } else {
      box(p, x, top - 0.1, z + end + dir * 0.45, width, 0.2, 0.9, m.stone);
      line(p, [x + width / 2, y + 0.9, z], [x + width / 2, top + 0.9, z + end], 0.04, m.iron);
    }
    return top;
  };

  /** An iron garden gate between two stone piers. */
  const gate = (p: THREE.Object3D, x: number, y: number, z: number, w = 1.1, axis: 'x' | 'z' = 'x'): void => {
    for (const s of [-1, 1]) {
      const px = axis === 'x' ? x + (s * (w + 0.36)) / 2 : x;
      const pz = axis === 'x' ? z : z + (s * (w + 0.36)) / 2;
      box(p, px, y + 0.7, pz, 0.36, 1.4, 0.36, m.stone);
      box(p, px, y + 1.45, pz, 0.46, 0.1, 0.46, m.cream);
    }
    for (let i = 0; i < 7; i += 1) {
      const t = (i / 6 - 0.5) * w;
      const bx = axis === 'x' ? x + t : x;
      const bz = axis === 'x' ? z : z + t;
      box(p, bx, y + 0.62 + Math.cos((i / 6 - 0.5) * Math.PI) * 0.12, bz, 0.035, 1.1 + Math.cos((i / 6 - 0.5) * Math.PI) * 0.24, 0.035, m.iron);
    }
    box(p, x, y + 0.3, z, axis === 'x' ? w : 0.04, 0.05, axis === 'x' ? 0.04 : w, m.iron);
  };

  /** A small orchard or yard tree in the town's foliage-card style. */
  const tree = (p: THREE.Object3D, x: number, y: number, z: number, size = 1, autumn = false): void => {
    cylinder(p, x, y + size * 1.1, z, 0.11 * size, 2.2 * size, m.darkWood, 0.6);
    for (let i = 0; i < 4; i += 1) {
      const a = i * 2.4;
      const spread = i === 0 ? 0 : size * 0.62;
      const leaf = mesh(p, plane, autumn ? leaf1 : leaf0, x + Math.cos(a) * spread, y + (2.3 + random() * 0.3) * size, z + Math.sin(a) * spread, 2.1 * size, 2.1 * size, 1);
      leaf.rotation.set(-0.3, (random() - 0.5) * 0.4, 0);
    }
  };

  /** A low leafy clump. */
  const bush = (p: THREE.Object3D, x: number, y: number, z: number, s = 0.8): void => {
    const leaf = mesh(p, plane, leaf0, x, y + s * 0.42, z, s * 1.3, s, 1);
    leaf.rotation.set(-0.3, (random() - 0.5) * 0.5, 0);
  };

  /** A lean-to shed with a plank door. */
  const shed = (p: THREE.Object3D, x: number, y: number, z: number, w = 1.8, d = 1.3, face: 'south' | 'east' = 'south'): void => {
    const h = 1.5;
    if (face === 'south') kit.box(x, y + h / 2, z, w, h, d, { top: null, front: C.shedDoor, east: C.planks, west: C.planks, back: C.planks });
    else kit.box(x, y + h / 2, z, d, h, w, { top: null, front: C.planks, east: C.shedDoor, west: C.planks, back: C.planks });
    const g = new THREE.Group();
    g.position.set(x, y + h + 0.18, z);
    if (face !== 'south') g.rotation.y = -Math.PI / 2;
    p.add(g);
    const roof = box(g, 0, 0, 0, w + 0.3, 0.08, d + 0.4, m.roofDark);
    roof.rotation.x = 0.22;
  };

  /** A deep shadowed alley slot between two backdrop houses: a shade card at its end and a dark runner on its floor. */
  const alley = (x0: number, x1: number, z0: number, z1: number, y: number, h: number): void => {
    kit.quad([x0, y, z0], [x1, y, z0], [x1, y + h, z0], [x0, y + h, z0], C.shade, [1, 1, 1], [0, 0, 1, 1], [0.7, 1]);
    kit.quad([x0, y + 0.07, z1], [x1, y + 0.07, z1], [x1, y + 0.07, z0], [x0, y + 0.07, z0], C.shade, [0.9, 0.9, 1], [0, 0.05, 1, 0.6], [1, 0.55]);
  };

  return { laundry, woodpile, vegPlot, hedge, lowWall, barrel, crate, cart, well, wallStair, gate, tree, bush, shed, alley };
};
